import { memo, useMemo } from "react";

import "@/styles/plain-preview-tokens.css";

import { getURL } from "@/lib/aws-amplify/storage";
import type {
	LangCode,
	PlainFontFamily,
	PlainReservationComponentType,
	PlainSizeType,
	PlainWidgetItemInput,
	S3Object,
} from "@/lib/gql/__generated__/graphql";
import { PlainWidgetDecoration } from "@/lib/gql/__generated__/graphql";

import type * as FormModelType from "@/components/features/design/plain/model/type";
import * as Shared from "@/components/features/design/plain/ui/components/main/_shared";

import { LikeReservationComponent } from "../like-reservation-component";
import { About } from "./about";
import { Cover } from "./cover";
import { FacilityInfo } from "./facility-info";
import { FacilityOverviewCard } from "./facility-overview-card";
import { PlanList, RoomList } from "./list";
import { WidgetItem } from "./widget-item";

type Props = {
	colorBackground: string;
	plainFontFamily: PlainFontFamily;
	fontColor: string;
	primaryColor: string;

	// cover
	mainKeyVisual?: S3Object;
	logoImage: FormModelType.PlainSettingFormModel[ "ViewModel" ][ "logoImage" ];
	isShowHotelLogo: boolean;
	isShowHotelKeyVisual: boolean;
	logoSize: PlainSizeType;
	isShowHotelCatchCopy: boolean;

	// about
	hotelName: string;
	hotelCatchCopy: string;
	isShowHotelName: boolean;

	// reservation
	reservationComponentType: PlainReservationComponentType;
	widgetDecoration: PlainWidgetDecoration;

	// snack
	snacks: FormModelType.QueryData[ "snackList" ][ "snacks" ];
	plainWidgetItems: Array<PlainWidgetItemInput>;

	// magazine
	rooms: FormModelType.QueryData[ "roomList" ][ "rooms" ];
	plans: FormModelType.QueryData[ "planList" ][ "plans" ];
	isShowMagazineAbout: boolean;
	isShowMagazinePlan: boolean;
	isShowMagazineRoom: boolean;

	//
	lang: LangCode;
};

/**
 * ReservationPane: Plain デザインプレビュー全体
 *
 * ゲスト側 app/frontend/guest/plain/src/pages/[lang]/index.astro の構造を React で再現:
 *   Hero → About（ホテル名/キャッチ）→ Reservation → Snack → FacilityOverview
 *   → PlanList → RoomList → FacilityInfo → FloatingReservationBar
 *
 * 値の一次ソースはゲスト実装。変更時は必ずゲスト側を確認・同期すること。
 * スタイルトークンは plain-preview-tokens.css（.plain-preview class）で定義。
 */
export const ReservationPane = memo(
	({
		colorBackground,
		plainFontFamily,
		fontColor,
		primaryColor,

		// cover
		mainKeyVisual,
		logoImage,
		isShowHotelLogo,
		isShowHotelKeyVisual,
		logoSize,

		// about
		hotelName,
		hotelCatchCopy,
		isShowHotelName,
		isShowHotelCatchCopy,

		// reservation
		reservationComponentType,
		widgetDecoration,

		// snack
		snacks,
		plainWidgetItems,

		// magazine
		rooms,
		plans,
		isShowMagazineAbout,
		isShowMagazinePlan,
		isShowMagazineRoom,

		//
		lang,
	}: Props) => {
		const fontFamilyClassName = useMemo(
			() => Shared.Constants.PlainFontFamilyClassNames[ plainFontFamily ],
			[ plainFontFamily ],
		);

		const mainKeyVisualUrl = useMemo(() => (mainKeyVisual ? getURL(mainKeyVisual) : ""), [ mainKeyVisual ]);

		const logoUrl = useMemo(
			() =>
				logoImage
					? getURL({
						bucket: logoImage.bucket?.value,
						key: logoImage.key?.value,
						region: logoImage.region?.value,
						mimeType: logoImage.mimeType?.value,
						fileName: logoImage.fileName?.value,
					})
					: "",
			[ logoImage ],
		);

		const activeSnackList = useMemo(
			() =>
				plainWidgetItems
					.map((item) => snacks.find((snack) => snack.id === item.contentID))
					.filter((item): item is NonNullable<typeof item> => item !== undefined),
			[ plainWidgetItems, snacks ],
		);

		return (
			<div className="plain-preview relative w-full" style={{ background: colorBackground }}>
				<Cover
					isShowHotelKeyVisual={isShowHotelKeyVisual}
					isShowLogo={isShowHotelLogo}
					mainKeyVisualUrl={mainKeyVisualUrl}
					logoUrl={logoUrl}
					logoSize={logoSize}
				/>

				<main className="mx-auto w-full px-4 pb-8 tablet:max-w-3xl tablet:px-6 laptop:max-w-4xl laptop:px-8">
					<div className="mt-5 laptop:mt-8">
						<About
							isShowHotelName={isShowHotelName}
							isShowHotelCatchCopy={isShowHotelCatchCopy}
							hotelName={hotelName}
							hotelCatchCopy={hotelCatchCopy}
							fontFamilyClassName={fontFamilyClassName}
							fontColor={fontColor}
						/>
					</div>

					<section className="mt-7 min-h-[80px] laptop:mt-10">
						<LikeReservationComponent
							primaryColor={primaryColor}
							plainReservationComponentType={reservationComponentType}
						/>
					</section>

					{activeSnackList.length > 0 && (
						<section className="mt-12">
							<div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 tablet:gap-x-5 tablet:gap-y-7">
								{activeSnackList.map((snack, index) => (
									<WidgetItem
										key={snack.uuid + index}
										thumbnail={snack?.thumbnail ?? ""}
										title={snack?.title ?? ""}
										primaryColor={primaryColor}
										widgetDecoration={widgetDecoration ?? PlainWidgetDecoration.None}
										fontFamily={fontFamilyClassName}
										fontColor={fontColor}
									/>
								))}
							</div>
						</section>
					)}

					{isShowMagazineAbout && (
						<section className="mt-12">
							<FacilityOverviewCard
								hotelName={hotelName}
								catchCopy={hotelCatchCopy}
								mainKeyVisualUrl={mainKeyVisualUrl}
								primaryColor={primaryColor}
							/>
						</section>
					)}

					{isShowMagazinePlan && (
						<section className="pt-14">
							<PlanList
								plans={plans}
								primaryColor={primaryColor}
								fontFamilyClassName={fontFamilyClassName}
								styleColor={fontColor}
								lang={lang}
							/>
						</section>
					)}

					{isShowMagazineRoom && (
						<section className="pt-14">
							<RoomList
								rooms={rooms}
								fontFamilyClassName={fontFamilyClassName}
								styleColor={fontColor}
								lang={lang}
							/>
						</section>
					)}

					<section className="pt-7 laptop:pt-24">
						<FacilityInfo hotelName={hotelName} />
					</section>
				</main>
			</div>
		);
	},
);
